// TODO: if we receive unread messages for a thread and we're currently in that thread then send ENTER_SCHEDULE_MESSAGE_THREAD to the server (another tab must have left the thread)

import { useRef, useState, useCallback, useEffect, Fragment } from 'react';
import { connect } from 'react-redux';
import { 
  SET_SCHEDULE_MESSAGES_DIALOGUE_ID, ADD_SCHEDULE_MESSAGE_THREAD, GET_SCHEDULE_MESSAGE_THREADS, ADD_SCHEDULE_MESSAGE, GET_SCHEDULE_MESSAGES, SET_ALERT,
  ENTER_SCHEDULE_MESSAGE_THREAD, LEAVE_SCHEDULE_MESSAGE_THREAD, WEBSOCKET_AUTHORIZED, MAX_TIMESTAMP,
} from '../actions/types';
import { siteboardAction } from '../actions/defaultActions';
import { format } from 'date-fns';
import { keyCountInObject } from '../Util';

import PopupWindow from './PopupWindow';
import AlertCountBubble from './AlertCountBubble';
import UserAvatar from './UserAvatar';
import PushButton from './PushButton';

// icons
import Logo from '../SiteBoard_Logo';
import WriteIcon from '../icons/writeIcon';
import SendMessageIcon from '../icons/sendMessageIcon';
import SendIcon from '../icons/sendIcon';
  
// test cases
const testScrollPanes = false; // add a bunch of copies to scrollable area to see what they look like when full of data
const testEmptyThreadList = false; // show the components as if there were no data to populate it

const ScheduleMessagesDialogue = ({ _users, _userId, _projects, _subscriptions, _schedules, _scheduleMessageThreads, _scheduleMessages, _scheduleMessagesDialogueProjectId, _scheduleMessagesDialogueThreadId, _websocketState, _unreadScheduleMessageThreads, siteboardAction }) => {

  const [inputMessage, setInputMessage] = useState('');
  const [activeThread, setActiveThread] = useState(null);
  const [activeThreadEarliestUnreadTimestamp, setActiveThreadEarliestUnreadTimestamp] = useState(null);
  const [activeThreadUnreadCount, setActiveThreadUnreadCount] = useState(0);
  const [currentTimestampReference, setCurrentTimestampReference] = useState(null); // used to periodically trigger an update of the displayed elapsed time of messages
  const [isLoadingThreads, setIsLoadingThreads] = useState(true);
  const [areMessageForThreadLoaded, setAreMessageForThreadLoaded] = useState({});

  const inputMessageRef = useRef(null);
  const messageScrollDivRef = useRef(null);
  const latestMessageTimestampForThreadId = useRef({}); // used for determining if a new message has been added to a thread so, we can scroll to the bottom of the page
  const messagesJustLoadedForThreadId = useRef({}); // used to determine when the initial thread messages are fetched from the server, so we can scroll the thread to the bottom
  const moreMessagesJustLoadedForThreadId = useRef({}); // used to determine when the more thread messages are fetched from the server, so we can scroll to where the user was
  const lastWebsocketState = useRef(_websocketState); // used to determine if websocket has reconnected, so we can signal to the server that we've rejoined a thread
  const haveSwitchedToRequestedThread = useRef(false);

  const selectedProject = _projects[_scheduleMessagesDialogueProjectId];
  const selectedSubscription = selectedProject ? _subscriptions[selectedProject.subscriptionId] : undefined;

  const enterMessageThread = useCallback((scheduleMessageThreadId) => {
    setActiveThread(scheduleMessageThreadId);
    if(scheduleMessageThreadId in _unreadScheduleMessageThreads && _unreadScheduleMessageThreads[scheduleMessageThreadId].unreadCount > 0) {
      setActiveThreadEarliestUnreadTimestamp(_unreadScheduleMessageThreads[scheduleMessageThreadId].earliestUnreadTimestamp);
      setActiveThreadUnreadCount(_unreadScheduleMessageThreads[scheduleMessageThreadId].unreadCount);
    } else {
      setActiveThreadEarliestUnreadTimestamp(null);
      setActiveThreadUnreadCount(0);
    }
    siteboardAction(ENTER_SCHEDULE_MESSAGE_THREAD, { 
      projectId: selectedProject.id,
      scheduleMessageThreadId: scheduleMessageThreadId
    });

    // Fetch messages for the thread
    const onSuccess = () => { 
      messagesJustLoadedForThreadId.current[scheduleMessageThreadId] = true; 
      setAreMessageForThreadLoaded({
        ...areMessageForThreadLoaded,
        [scheduleMessageThreadId]: true,
      });
    };
    siteboardAction(GET_SCHEDULE_MESSAGES, {
      projectId: selectedProject.id,
      scheduleMessageThreadId: scheduleMessageThreadId,
      fetchBeforeTimestamp: MAX_TIMESTAMP,
    }, onSuccess);
  }, [_unreadScheduleMessageThreads, areMessageForThreadLoaded, selectedProject, siteboardAction]);

  // Load schedule threads and maybe messages
  useEffect(() => {
    if(selectedProject) {
      const onSuccess = () => { setIsLoadingThreads(false); };
      siteboardAction(GET_SCHEDULE_MESSAGE_THREADS, { projectId: selectedProject.id, }, onSuccess);
    }

    // Switch to the thread specified by the input parameter when the component first loads
    if(!haveSwitchedToRequestedThread.current && (_scheduleMessagesDialogueThreadId in _unreadScheduleMessageThreads || _scheduleMessagesDialogueThreadId in _scheduleMessageThreads)) {
      haveSwitchedToRequestedThread.current = true;
      enterMessageThread(_scheduleMessagesDialogueThreadId);
    }
  }, [selectedProject, siteboardAction, _scheduleMessageThreads, _scheduleMessagesDialogueThreadId, _unreadScheduleMessageThreads, enterMessageThread]);

  // If websocket reconnects and we're in a message thread, inform server that user has re-entered the thread
  // We need to do this because the server automatically registers the user as having left all threads if their websocket disconnects
  useEffect(() => {
    if(_websocketState === WEBSOCKET_AUTHORIZED && lastWebsocketState.current !== WEBSOCKET_AUTHORIZED && (activeThread in _scheduleMessageThreads)) {
      siteboardAction(ENTER_SCHEDULE_MESSAGE_THREAD, { 
        projectId: _scheduleMessageThreads[activeThread].projectId,
        scheduleMessageThreadId: _scheduleMessageThreads[activeThread].id
      });
    }
    lastWebsocketState.current = _websocketState;
  }, [_websocketState, activeThread, _scheduleMessageThreads, siteboardAction]);

  // Refresh message elapsed timestamps periodically by updating the current reference timestamp
  useEffect(() => {
    const updateInterval = setInterval(() => {
      setCurrentTimestampReference(new Date().getTime());
    }, 20000);
    return () => { 
      clearInterval(updateInterval); // remove listener when component unloads
    };
  }, []);

  // Scroll the messages window to the bottom instantly if changing the page (if it is visible);
  useEffect(() => {
    if (messageScrollDivRef.current) {
      messageScrollDivRef.current.scrollTo({ top: messageScrollDivRef.current.scrollHeight, });
    }
  }, [activeThread]);

  // Scroll the messages window to the bottom smoothly if a new message is added to the thread being viewed
  useEffect(() => {
    let haveNewMessageForCurrentThread = false;

    // Update latest message timestamp for each thread
    for (const messageId in _scheduleMessages) {
      const message = _scheduleMessages[messageId];
      if (!(message.scheduleMessageThreadId in latestMessageTimestampForThreadId.current) || message.timestamp > latestMessageTimestampForThreadId.current[message.scheduleMessageThreadId]) {
        latestMessageTimestampForThreadId.current[message.scheduleMessageThreadId] = message.timestamp;
        if(activeThread === message.scheduleMessageThreadId) {
          haveNewMessageForCurrentThread = true;
        }
      }
    }

    if(activeThread !== null && messagesJustLoadedForThreadId.current[activeThread] === true && messageScrollDivRef.current) {
      // A bunch of messages have been fetched from the server. fast scroll the message window to the bottom
      messageScrollDivRef.current.scrollTo({ top: messageScrollDivRef.current.scrollHeight });
      messagesJustLoadedForThreadId.current[activeThread] = false;
    } else if (activeThread !== null && activeThread in moreMessagesJustLoadedForThreadId.current && messageScrollDivRef.current) {
      // More messages for the thread have just been added. Set the message window scroll the where the user was before
      const previousEarliestMessageElement = document.getElementById(moreMessagesJustLoadedForThreadId.current[activeThread].earliestMessageIdBeforeNewMessage);
      if(!previousEarliestMessageElement) return;
      messageScrollDivRef.current.scrollTo({ top: previousEarliestMessageElement.offsetTop + moreMessagesJustLoadedForThreadId.current[activeThread].scrollBeforeNewMessages});
      delete moreMessagesJustLoadedForThreadId.current[activeThread];
    } else if (haveNewMessageForCurrentThread && messageScrollDivRef.current) {
      // A new message has come in from another user. smooth scroll message window to the bottom
      messageScrollDivRef.current.scrollTo({
        top: messageScrollDivRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [_scheduleMessages, activeThread]);

  const getShorthandElapsedSinceTimestamp = (timestamp) => {
    let timeSinceLatestMessage = '';
    const millisecondsElapsed = new Date().getTime() - timestamp;
    const secondsElapsed = millisecondsElapsed / 1000;
    const minutesElapsed = secondsElapsed / 60;
    const hoursElapsed = secondsElapsed / 3600;
    const daysElapsed = secondsElapsed / 86400;

    if(daysElapsed >= 1) {
      timeSinceLatestMessage = Math.round(daysElapsed) + "d";
    } else if(hoursElapsed >= 1) {
      timeSinceLatestMessage = Math.round(hoursElapsed) + "h";
    } else if(minutesElapsed >= 1) {
      timeSinceLatestMessage = Math.round(minutesElapsed) + "m";
    } else {
      timeSinceLatestMessage = parseInt(secondsElapsed) + "s";
    }

    return timeSinceLatestMessage;
  };

  const getUserAvatar = (user, forScheduleMessageThread) => {
    let userIsInThread = false;
    if(forScheduleMessageThread) {
      const threadMember = forScheduleMessageThread.members[user.id];
      userIsInThread = threadMember.enteredThreadTimestamp > user.lastOnlineTimestamp && threadMember.enteredThreadTimestamp > threadMember.leftThreadTimestamp;
    }
    
    let userStatus = 'offline';
    if(userIsInThread) {
      userStatus = 'chatting';
    } else if(user.isOnline) {
      userStatus = 'online';
    }

    return (
      <div className='relative flex justify-center items-center uppercase bg-[#f99917] rounded-full p-1 w-9 h-9 min-h-9 min-w-9 text-center text-black font-semibold'>
        <div>{user.firstName[0]}{user.lastName[0]}</div>
        <div className={`absolute bottom-0 translate-y-[70%] ${userStatus === 'offline' ? 'text-red-400' : 'text-green-400'} text-[0.6em] py-[2px] px-1 bg-[#444] rounded`}>
          {userStatus}
        </div>
      </div>
    );
  };

  const threadPreviewJSX = (scheduleMessageThread, index) => {
    const memberIds = [];
    for(const memberId in scheduleMessageThread.members) {
      memberIds.push(memberId);
    }

    let messagesCount = 0;
    for(const scheduleMessageThreadMemberId in scheduleMessageThread.members) {
      messagesCount += scheduleMessageThread.members[scheduleMessageThreadMemberId].messageCount;
    }

    const otherMemberIds = memberIds.filter((memberId) => memberId !== _userId);
    if(otherMemberIds > 1) return (<div>group thread ({otherMemberIds.length})</div>);
    if(!(otherMemberIds[0] in _users)) return (<div className='py-3'>Thread with removed project member, {messagesCount} message{messagesCount !== 1 ? 's' :  ''}</div>);
    const otherMemberId = otherMemberIds[0];

    let latestThreadMessage = null;
    for(const messageId in _scheduleMessages) {
      if(_scheduleMessages[messageId].scheduleMessageThreadId !== scheduleMessageThread.id) continue;
      if(latestThreadMessage === null || _scheduleMessages[messageId].timestamp > latestThreadMessage.timestamp) {
        latestThreadMessage = _scheduleMessages[messageId];
      }
    }

    const unreadMessageCountInThread = scheduleMessageThread.id in _unreadScheduleMessageThreads ? _unreadScheduleMessageThreads[scheduleMessageThread.id].unreadCount : 0;

    return (
      <button 
        className='flex justify-center items-start gap-x-2 w-full hover:bg-[#555] px-1 sm:px-2 py-3 rounded-lg'
        onClick={() => { enterMessageThread(scheduleMessageThread.id); }}
        onTouchStart = {(event) => {
          siteboardAction(GET_SCHEDULE_MESSAGES, { // Preemptively fetch
            projectId: selectedProject.id,
            scheduleMessageThreadId: scheduleMessageThread.id,
            fetchBeforeTimestamp: MAX_TIMESTAMP,
          });
        }}
        onMouseDown = {(event) => {
          siteboardAction(GET_SCHEDULE_MESSAGES, { // Preemptively fetch
            projectId: selectedProject.id,
            scheduleMessageThreadId: scheduleMessageThread.id,
            fetchBeforeTimestamp: MAX_TIMESTAMP,
          });
        }}
      >
        {getUserAvatar(_users[otherMemberId], scheduleMessageThread)}
        <div className='grow flex flex-col gap-1 text-left overflow-hidden'>
          <div className='whitespace-nowrap overflow-hidden leading-[1.2em]'>
            <p className='inline font-semibold '>{_users[otherMemberId].firstName} {_users[otherMemberId].lastName}</p> {latestThreadMessage !== null && <p className='inline-block opacity-50'> ({_users[otherMemberId].email})</p>}
          </div>
          <div className={`overflow-hidden max-h-[2.4em] leading-[1.2em] ${unreadMessageCountInThread > 0 ? 'font-bold' : ''}`}>
            {latestThreadMessage === null ? <div className='text-white/50'>{_users[otherMemberId].email}</div> : latestThreadMessage.content}

                    
          </div>
        </div>
        {latestThreadMessage !== null &&
          <div className='flex flex-col self-start text-sm gap-y-1'>
            <div>{getShorthandElapsedSinceTimestamp(latestThreadMessage.timestamp)}</div>
            {unreadMessageCountInThread > 0 &&
              <AlertCountBubble totalUnreadMessages={unreadMessageCountInThread} />
            }
          </div>
        }
      </button>
    );
  };

  const listThreadsPageJSX = () => {
    if (isLoadingThreads) {
      return (
        <div className={`grow flex flex-col justify-center gap-y-1 p-1`}>
          <div className='flex rounded-[10px] py-6 items-center justify-center font-semibold text-[1.5em] gap-x-4'>
            <div className='animate-[spin_1s_linear_infinite]'>
              <Logo size={48} />
            </div>
            <p className='select-none'>Loading Messages</p>
          </div>
        </div>
      );
    } 

    // Create a list of existing message threads
    const scheduleMessageThreadsList = [];
    const alreadyConversingWithMemberIds = {};
    for(const threadId in _scheduleMessageThreads) {
      const thread = _scheduleMessageThreads[threadId];

      if(testEmptyThreadList) continue;
      if(thread.projectId !== selectedProject.id) continue;
      if(!(_userId in thread.members)) continue;

      // Make a list of members the user has already created a thread with for this schedule
      for(const userId in thread.members) {
        alreadyConversingWithMemberIds[userId] = '';
      }

      // Find the latest message for this thread
      let latestThreadMessage = null;
      for(const messageId in _scheduleMessages) {
        const message = _scheduleMessages[messageId];
        if(message.scheduleMessageThreadId !== thread.id) continue;

        if(latestThreadMessage === null || message.timestamp > latestThreadMessage.timestamp) {
          latestThreadMessage = message;
        }
      }

      for(let i = 0; i < (testScrollPanes ? 10 : 1); i++) {
        scheduleMessageThreadsList.push({thread: thread, latestMessageTimestamp: (latestThreadMessage === null ? 0 : latestThreadMessage.timestamp)});
      }
    }
    // Sort the message threads with latest message thread at the top
    scheduleMessageThreadsList.sort((one, two) => two.latestMessageTimestamp - one.latestMessageTimestamp);

    const newChatPartners = [];
    const newChatPartnersIdMap = {};
    for(const userId in selectedProject.members) {
      newChatPartnersIdMap[userId] = '';
    }
    for(const userId in selectedSubscription.members) {
      if(selectedSubscription.members[userId].role === 'Owner' || selectedSubscription.members[userId].role === 'Admin') {
        newChatPartnersIdMap[userId] = '';
      }
    }
    for(const userId in newChatPartnersIdMap) {
      if(testEmptyThreadList) continue;
      if(!(userId in _users)) continue;
      if(testScrollPanes) {
        for(let i = 0; i < 10 ; i++) {
          newChatPartners.push(_users[userId]);
        }
      }
      if(userId === _userId) continue;
      if(userId in alreadyConversingWithMemberIds) continue;
      newChatPartners.push(_users[userId]);
    }
    // Sort new chat partners by last name
    newChatPartners.sort((one, two) => {
      return one.lastName.toLowerCase().localeCompare(two.lastName.toLowerCase());
    });

    if((scheduleMessageThreadsList.length + newChatPartners.length) > 0) {
      return (
        <div className='grow flex flex-col gap-2 mt-2'>
          <div>Choose someone to chat with about this schedule:</div>
          <div className='grow flex flex-col gap-y-1 basis-0 overflow-y-auto'>
            {scheduleMessageThreadsList.map((scheduleMessageThread, index) => (
              <div key={index}>
                {threadPreviewJSX(scheduleMessageThread.thread, index)}
              </div>
            ))}
            {newChatPartners.map((user, index) => (
              <button 
                key={index}
                className='flex justify-center items-start gap-x-2 w-full hover:bg-[#555] px-1 sm:px-2 py-3 rounded-lg'
                onClick={() => {
                  const onSuccess = (output) => { enterMessageThread(output.scheduleMessageThread.id); };
                  siteboardAction(ADD_SCHEDULE_MESSAGE_THREAD, {
                    projectId: selectedProject.id,
                    subscriptionId: selectedProject.subscriptionId,
                    memberIds: [_userId, user.id],
                  }, onSuccess);
                }}
              >
                {getUserAvatar(user)}
                <div className='grow flex flex-col gap-1 text-left overflow-hidden'>
                  <div className='font-semibold whitespace-nowrap overflow-hidden leading-[1.2em]'>
                    {user.firstName} {user.lastName}
                  </div>
                  <div className={`overflow-hidden max-h-[2.4em] leading-[1.2em]`}>
                    <div className='text-white/50'>{user.email}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      );
    } 

    return (
      <div className='grow flex flex-col justify-center items-center'>
        <div className='text-xl px-4 text-center'>Add another member to this project to enable messaging</div>
      </div>  
    );
  };

  const activeThreadMessagesJSX = () => {
    if (areMessageForThreadLoaded[activeThread] !== true || isLoadingThreads) {
      return (
        <div className={`grow flex flex-col justify-center gap-y-1 p-1`}>
          <div className='flex rounded-[10px] py-6 items-center justify-center font-semibold text-[1.5em] gap-x-4'>
            <div className='animate-[spin_1s_linear_infinite]'>
              <Logo size={48} />
            </div>
            <p className='select-none'>Loading Messages</p>
          </div>
        </div>
      );
    } 

    const scheduleMessageThread = _scheduleMessageThreads[activeThread];
    const memberIds = [];
    let messagesInThread = 0;
    for(const memberId in scheduleMessageThread.members) {
      memberIds.push(memberId);
      messagesInThread += scheduleMessageThread.members[memberId].messageCount;
    }
    const otherMemberIds = memberIds.filter((memberId) => memberId !== _userId);

    if(otherMemberIds > 1) return (<div>group thread ({otherMemberIds.length})</div>);
    if(!(otherMemberIds[0] in _users)) return (<div>user not found</div>);

    const otherMemberId = otherMemberIds[0];

    const threadMessages = [];
    for(const messageId in _scheduleMessages) {
      if(_scheduleMessages[messageId].scheduleMessageThreadId !== scheduleMessageThread.id) continue;
      for(let i = 0; i < (testScrollPanes ? 10 : 1); i++) {
        threadMessages.push(_scheduleMessages[messageId]);
      }
    }
    threadMessages.sort((one, two) => one.timestamp - two.timestamp);

    // Find the id of the earliest unread message so we can put the unread messages divider right above it
    let earliestUnreadMessageId = null;
    if(activeThreadEarliestUnreadTimestamp !== null) {
      for(const message of threadMessages) {
        if(message.timestamp === activeThreadEarliestUnreadTimestamp) {
          earliestUnreadMessageId = message.id;
          break;
        }
      }
    }

    return (
      <div className='grow flex flex-col gap-2'>
        <div className='flex items-center gap-x-2 px-1 py-2 self-start'>
          {getUserAvatar(_users[otherMemberId], scheduleMessageThread)}
          <div className='grow flex flex-col text-left overflow-hidden'>
            <div className='font-semibold leading-[1em] whitespace-nowrap overflow-hidden'>{_users[otherMemberId].firstName} {_users[otherMemberId].lastName}</div>
            <p className='inline-block text-sm text-white/50'>{_users[otherMemberId].email}</p>
          </div>
        </div>
        <div 
          ref={messageScrollDivRef}
          className='grow relative flex flex-col gap-y-2 basis-0 overflow-y-auto'
          onScroll={(event) => {
            if(!event.target) return;
            if(typeof event.target.scrollTop !== 'number') return;

            // load more messages if we currently have less message than the total amount in the thread and we've scroll to the top of the message pane
            if(threadMessages.length < messagesInThread && event.target.scrollTop < 1000) {
              const onSuccess = () => {
                moreMessagesJustLoadedForThreadId.current[scheduleMessageThread.id] = {
                  scrollBeforeNewMessages: event.target.scrollTop,
                  earliestMessageIdBeforeNewMessage: threadMessages[0].id,
                };
              };
              siteboardAction(GET_SCHEDULE_MESSAGES, { // get the fetch key and fetch more messages
                projectId: selectedProject.id,
                scheduleMessageThreadId: scheduleMessageThread.id,
                fetchBeforeTimestamp: threadMessages[0].timestamp, // get message before the timestamp of the earliest message we have
              }, onSuccess);
            }
          }}
        >
          {threadMessages.length === 0 &&
            <div className='grow flex flex-col justify-center items-center text-[#aaa] text-2xl'>
              <div>Enter a Message Below</div>
            </div>
          }
          {threadMessages.map((message, index) => (
            <Fragment key={index}>
              {earliestUnreadMessageId === message.id &&
                <div className='bg-[#aaa] w-full h-[1px] text-center my-8 leading-[2em] font-bold text-sm'>{activeThreadUnreadCount} unread message{activeThreadUnreadCount !== 1 ? 's' : ''}</div>
              }
              <div 
                className={`flex flex-col px-4 py-2 rounded-xl max-w-[80%] shadow-md ${message.ownerId === _userId ? 'self-end bg-[#471cff]' : 'self-start bg-[#555]'}`}
              >
                <div id={message.id} className='font-medium whitespace-pre-line break-all leading-[1.2em]'>{message.content}</div>
                <div className='text-[0.85em] opacity-70 self-end leading-[1.2em]'>{getShorthandElapsedSinceTimestamp(message.timestamp)} , {format(new Date(message.timestamp), 'MM/dd/yy @ h:mm aa')}</div>
              </div>
            </Fragment>
          ))}
        </div>
        <div className='flex items-end gap-1'>
          <textarea
            ref={inputMessageRef}
            className='w-full text-white leading-[1.4em] rounded-md px-3 py-2.5 bg-[#555] hover:bg-[#656565] focus:bg-[#656565] resize-none' 
            rows={1}
            type="text"
            placeholder='Type a message...'
            value={inputMessage}
            onChange={(event) => {
              setInputMessage(event.target.value); 
              // Adjust the height of the textarea to be up to 5 lines if the user types a lot
              const lineHeight = parseInt(getComputedStyle(inputMessageRef.current).lineHeight);
              inputMessageRef.current.style.height = 'auto';
              inputMessageRef.current.style.height = Math.min(lineHeight*5, inputMessageRef.current.scrollHeight)+'px';
            }}
          ></textarea>
          <PushButton 
            isInline={true}
            textColor='white'
            bgColor='#471cff'
            hoverBgColor='#603aff'
            onClick={() => {
              const trimmedMessage = inputMessage.trim();
              if(trimmedMessage.length === 0) {
                siteboardAction(SET_ALERT, { message: 'Enter a message before sending' });
                return;
              }

              const onSuccess = (output) => { 
                inputMessageRef.current.style.height = 'auto'; // reset input textarea height

                // Clear unread message divider
                setActiveThreadEarliestUnreadTimestamp(null);
                setActiveThreadUnreadCount(0);

                setInputMessage(''); // clear input message
              };
              siteboardAction(ADD_SCHEDULE_MESSAGE, {
                projectId: selectedProject.id,
                subscriptionId: selectedProject.subscriptionId,
                scheduleMessageThreadId: scheduleMessageThread.id,
                content: trimmedMessage,
              }, onSuccess);
            }}
          >
            <div className='flex items-center gap-x-[6px] px-4'>
              <SendIcon size='1.2em' />
              <div>Send</div>
            </div>
          </PushButton>
        </div>
      </div>
    );
  };

  const popupTitleJSX = () => {
    return (
      <div className='flex flex-col justify-center items-center'>
        <div className='text-[1.8em] font-semibold text-white'>Messages</div>
        <div className='text-center leading-[1.2em]'>
          <p className='inline-block ml-1 text-[#f9a928]'>{selectedProject.name}</p>
        </div>
      </div>
    );
  };

  const closeDialogue = () => {
    siteboardAction(SET_SCHEDULE_MESSAGES_DIALOGUE_ID, { projectId: '00000000-0000-0000-0000-000000000000', scheduleMessageThreadId: '00000000-0000-0000-0000-000000000000' });
    if(activeThread in _scheduleMessageThreads) {
      siteboardAction(LEAVE_SCHEDULE_MESSAGE_THREAD, { 
        projectId: _scheduleMessageThreads[activeThread].projectId,
        scheduleMessageThreadId: _scheduleMessageThreads[activeThread].id
      });
    }
  };

  const onBackButton = (() => {
    if(activeThread in _scheduleMessageThreads) {
      return () => {
        if(activeThread in _scheduleMessageThreads) {
          siteboardAction(LEAVE_SCHEDULE_MESSAGE_THREAD, { 
            projectId: _scheduleMessageThreads[activeThread].projectId, 
            scheduleMessageThreadId: _scheduleMessageThreads[activeThread].id
          });
        }
        setActiveThread(null);
      };
    } 
    return null;
  })();

  if(!selectedProject) return (<></>);

  return (
    <PopupWindow title={popupTitleJSX} closeDialogue={closeDialogue} bgStyle='bg-[#333]' onBackButton={onBackButton} isFullHeight={true}>
      <div className='grow flex flex-col max-w-screen-lg bg-[#333] text-white'>
        <div className='h-0 w-[100vw] max-w-full'></div>
        {(activeThread in _scheduleMessageThreads)
          ? activeThreadMessagesJSX()
          : listThreadsPageJSX()
        }
      </div>
    </PopupWindow>
  );
};

// define redux state that will be passed in as props
const mapStateToProps = state => { return {
  _users: state.serverSynced.users,
  _userId: state.localPersistent.userId,
  _projects: state.serverSynced.projects,
  _subscriptions: state.serverSynced.subscriptions,
  _schedules: state.serverSynced.schedules,
  _scheduleMessageThreads: state.serverSynced.scheduleMessageThreads,
  _scheduleMessages: state.serverSynced.scheduleMessages,
  _scheduleMessagesDialogueProjectId: state.localEphemeral.scheduleMessagesDialogueProjectId,
  _websocketState: state.localEphemeral.websocketState,
  _unreadScheduleMessageThreads: state.serverSynced.unreadScheduleMessageThreads,
  _scheduleMessagesDialogueThreadId: state.localEphemeral.scheduleMessagesDialogueThreadId,
}};
export default connect(mapStateToProps, { siteboardAction })(ScheduleMessagesDialogue);
